// Notes SC6a R2 — pure functions over the clinical note's Tiptap/ProseMirror JSON doc. No DOM, no Tiptap import: used by
// the app bundle (the plain-text mirror on every save, legacy conversion on open, the AI calls) and unit-tested in Node.
//
//  plainToDoc(text)       a plain note (all notes before SC6) → a doc (investigation 2026-09-27 §4 / decision 25.4)
//  docToPlain(doc)        the doc → the plain-text mirror stored in vault_notes.content. DETERMINISTIC: the same doc always
//                         gives the same string (the old-client rule, the conflict compare and the dirty checks depend on it)
//  findPlanSection(x)     the "Plan" section of a past note, rich or plain (for SC6b "Add Plan"; unit-tested now)
//  docHasInlineMarks(doc) true when any text carries bold / italic / underline / highlight (Tidy's marks notice)

// The eight known heading labels (the vc_* section headings; SC6c D3 added Homework, decision 28.13), EN + HE, exactly as
// in _STRINGS (the R2 harness checks this list against index.html). A plain line that is exactly one of these + ':'
// becomes a heading. (findPlanSection's boundaries need no list: any 'Label:' line / any heading ends the Plan section.)
export const KNOWN_HEADINGS = [
  'Goals', 'Observations', 'Progress', 'Plan', 'Recommendations', 'Summary', 'Background', 'Homework',
  'מטרות', 'תצפיות', 'התקדמות', 'תוכנית', 'המלצות', 'סיכום', 'רקע', 'שיעורי בית',
];
const _known = new Set(KNOWN_HEADINGS.map(s => s.toLowerCase()));
const PLAN_LABELS = ['plan', 'treatment plan', 'next steps', 'תוכנית', 'תכנית'];

export const HEADING_LEVEL = 3; // decision 25.3: one level, stored as 3

export function emptyDoc() { return { type: 'doc', content: [{ type: 'paragraph' }] }; }

const _text = (s) => (s ? [{ type: 'text', text: s }] : undefined);
const _para = (s, d) => { const p = { type: 'paragraph' }; if (d) p.attrs = { dir: d }; const c = _text(s); if (c) p.content = c; return p; };
const _item = (s, d) => ({ type: 'listItem', content: [_para(s, d)] });

// F-55b: a block's direction the specialist FORCED with the toolbar ('ltr' | 'rtl'); 'auto' / nothing = not forced.
const _forcedDir = (n) => { const d = n && n.attrs && n.attrs.dir; return d === 'ltr' || d === 'rtl' ? d : null; };
// Tidy rebuilds the note from plain text, which carries no direction. These two keep a forced direction on the line it belongs to:
// the result line inherits it when the result has the same number of lines as the source (by position) or, when the count differs,
// when its text is identical to a source line (in order); a positional match must keep the language class (Hebrew-dominant or
// not: Tidy never translates), so a reorder cannot hand a Hebrew line an English line's direction. Anything unmatched stays automatic.
const _normLine = (s) => String(s || '').replace(/[ \t ]+/g, ' ').replace(/^ ?(?:[-•*·]|\d{1,3}[.)]) /, '').trim();
const _langClass = (s) => { let he = 0, en = 0; for (const c of String(s || '')) { if (/[֐-׿]/.test(c)) he++; else if (/[A-Za-z]/.test(c)) en++; } return he + en === 0 ? 'none' : (he >= en ? 'he' : 'en'); };
export function alignLineDirs(resLines, srcLines, srcDirs) {
  const out = new Array(resLines.length).fill(null);
  if (resLines.length === srcLines.length) {
    for (let i = 0; i < resLines.length; i++) if (srcDirs[i] && _langClass(resLines[i]) === _langClass(srcLines[i])) out[i] = srcDirs[i];
    return out;
  }
  let j = 0;
  for (let i = 0; i < resLines.length; i++) {
    const n = _normLine(resLines[i]); if (!n) continue;
    for (let k = j; k < srcLines.length; k++) if (_normLine(srcLines[k]) === n) { out[i] = srcDirs[k] || null; j = k + 1; break; }
  }
  return out;
}

const RX_BULLET = /^\s*[-*•·]\s+(.*)$/;
const RX_ORDERED = /^\s*(\d{1,3})[.)]\s+(.*)$/;
const RX_LABEL = /^\s*(.+?)\s*:\s*$/;

// Plain → doc, line by line. Consecutive bullet lines join one bullet list; consecutive numbered lines join one ordered
// list (its start = the first number). A line that is exactly a known heading label + ':' becomes a heading. An empty
// line is an empty paragraph (keeps the author's spacing). Anything else: a paragraph, verbatim.
// opts.extraHeadings (Tidy, decision 35.7): the note's own heading labels — a "<label>:" line for one of them is a heading
// too, for this call only (so a custom heading survives a Tidy round trip).
// opts.srcLines + opts.lineDirs (Tidy, F-55b): the lines the text was built from and each line's forced direction; the result keeps a
// forced direction on the matching line (alignLineDirs). Without them the output is exactly what it was before F-55b.
export function plainToDoc(text, opts = {}) {
  const extra = new Set((opts.extraHeadings || []).map(s => String(s).toLowerCase()));
  const lines = String(text == null ? '' : text).replace(/\r\n?/g, '\n').split('\n');
  const dirs = (opts.lineDirs && opts.srcLines && opts.lineDirs.some(Boolean)) ? alignLineDirs(lines, opts.srcLines, opts.lineDirs) : null;
  const out = []; let list = null; let li = -1;
  const flush = () => { if (list) { out.push(list); list = null; } };
  for (const line of lines) {
    li++; const d = dirs ? dirs[li] : null;
    let m;
    if ((m = line.match(RX_BULLET)) && m[1].trim()) {
      if (!list || list.type !== 'bulletList') { flush(); list = { type: 'bulletList', content: [] }; }
      list.content.push(_item(m[1], d));
      continue;
    }
    if ((m = line.match(RX_ORDERED)) && m[2].trim()) {
      if (!list || list.type !== 'orderedList') { flush(); list = { type: 'orderedList', attrs: { start: parseInt(m[1], 10) || 1 }, content: [] }; }
      list.content.push(_item(m[2], d));
      continue;
    }
    flush();
    if ((m = line.match(RX_LABEL)) && (_known.has(m[1].toLowerCase()) || extra.has(m[1].toLowerCase()))) {
      out.push({ type: 'heading', attrs: d ? { level: HEADING_LEVEL, dir: d } : { level: HEADING_LEVEL }, content: _text(m[1]) });
      continue;
    }
    out.push(_para(line, d));
  }
  flush();
  // A single empty paragraph for an empty note (a doc needs one block).
  if (!out.length) out.push(_para(''));
  return { type: 'doc', content: out };
}

// Inline content → text: marks dropped, hardBreak → '\n'.
function _inline(node) {
  let s = '';
  for (const c of node.content || []) {
    if (c.type === 'text') s += c.text || '';
    else if (c.type === 'hardBreak') s += '\n';
    else s += _inline(c);
  }
  return s;
}

// A list item: the first block carries the marker; following blocks are indented under it; nested lists go one level deeper.
// `dirs` (optional, F-55b): a parallel array that gets each pushed line's FORCED direction (see _forcedDir); omitted = unchanged behaviour.
function _listLines(list, depth, lines, dirs) {
  const ordered = list.type === 'orderedList';
  const start = ordered ? ((list.attrs && Number.isFinite(+list.attrs.start)) ? +list.attrs.start : 1) : 0;
  const pad = '  '.repeat(depth);
  (list.content || []).forEach((item, i) => {
    const marker = ordered ? (start + i) + '. ' : '• ';
    let first = true;
    for (const b of item.content || []) {
      if (b.type === 'bulletList' || b.type === 'orderedList') { _listLines(b, depth + 1, lines, dirs); first = false; continue; }
      const t = _inline(b);
      if (first) { lines.push(pad + marker + t); first = false; }
      else lines.push(pad + ' '.repeat(marker.length) + t);
      if (dirs) dirs.push(_forcedDir(b));
    }
    if (first) { lines.push(pad + marker); if (dirs) dirs.push(null); } // an empty item
  });
}

// Doc → the plain-text mirror (investigation §2): one line per block, headings on their own line (no '#', no colon),
// '• ' bullets, 'N. ' numbers (from the list's start), blocks joined with a single '\n', marks dropped.
export function docToPlain(doc) {
  if (!doc || !Array.isArray(doc.content)) return '';
  const lines = [];
  for (const b of doc.content) {
    if (b.type === 'bulletList' || b.type === 'orderedList') _listLines(b, 0, lines);
    else lines.push(_inline(b));
  }
  // An empty doc (a single empty paragraph) is the empty string.
  if (lines.length === 1 && lines[0] === '') return '';
  return lines.join('\n');
}

// Tidy's input (decision 35.7): docToPlain's dialect, but EVERY heading is written "<label>:" (the clinic's 8 and the
// specialist's own), so the model — and the server's heading check — can tell a heading from a short line.
// → { text, headings: [the note's heading labels, in order], lineDirs: one forced direction ('ltr' | 'rtl' | null) per line of `text` (F-55b) }
export function docToTidyText(doc) {
  if (!doc || !Array.isArray(doc.content)) return { text: '', headings: [], lineDirs: [] };
  const lines = [], headings = [], dirs = [];
  for (const b of doc.content) {
    if (b.type === 'bulletList' || b.type === 'orderedList') _listLines(b, 0, lines, dirs);
    else if (b.type === 'heading') { const h = _inline(b).trim(); if (h) { headings.push(h); lines.push(h + ':'); } else lines.push(''); dirs.push(_forcedDir(b)); }
    else { lines.push(_inline(b)); dirs.push(_forcedDir(b)); }
  }
  if (lines.length === 1 && lines[0] === '') return { text: '', headings, lineDirs: [] };
  // a paragraph with a hard break is one block but several lines of text: each of its lines carries the block's direction
  const lineDirs = lines.flatMap((l, i) => new Array(l.split('\n').length).fill(dirs[i]));
  return { text: lines.join('\n'), headings, lineDirs };
}
// A list nested inside a list item (Tidy flattens it — decision 35.8's notice).
export function docHasNestedList(doc) {
  let found = false;
  (function walk(n, inItem) { if (found || !n) return; if (inItem && (n.type === 'bulletList' || n.type === 'orderedList')) { found = true; return; } (n.content || []).forEach(c => walk(c, inItem || n.type === 'listItem')); })(doc, false);
  return found;
}

export function docHasInlineMarks(doc) {
  let found = false;
  (function walk(n) { if (found || !n) return; if (n.type === 'text' && Array.isArray(n.marks) && n.marks.length) { found = true; return; } (n.content || []).forEach(walk); })(doc);
  return found;
}

export function isDocEmpty(doc) { return docToPlain(doc).trim() === ''; }

// The "Plan" section of a past note → { label, content: [block nodes] } or null (no Plan section, or an empty one).
//  • rich: a heading whose text (trimmed, case-insensitive, trailing ':' ignored) is a Plan label; the section runs to
//    the next heading;
//  • plain: a line starting with a Plan label + ':' / '-' / '–' (inline text after it counts); the section runs to the
//    next known-label line ('Label:' alone on a line) or the end. Converted with plainToDoc.
const _norm = (s) => String(s || '').trim().replace(/\s*:\s*$/, '').trim().toLowerCase();
export function findPlanSection(x) {
  if (x && typeof x === 'object' && Array.isArray(x.content)) {
    const blocks = x.content;
    const i = blocks.findIndex(b => b.type === 'heading' && PLAN_LABELS.includes(_norm(_inline(b))));
    if (i < 0) return null;
    const body = [];
    for (let j = i + 1; j < blocks.length && blocks[j].type !== 'heading'; j++) body.push(blocks[j]);
    const trimmed = _trimEmpty(body);
    return trimmed.length ? { label: _inline(blocks[i]).trim(), content: trimmed } : null;
  }
  const lines = String(x == null ? '' : x).replace(/\r\n?/g, '\n').split('\n');
  const rx = /^\s*(plan|treatment plan|next steps|תוכנית|תכנית)\s*[:\-–]\s*(.*)$/i;
  const i = lines.findIndex(l => rx.test(l));
  if (i < 0) return null;
  const m = lines[i].match(rx);
  const body = [];
  if (m[2] && m[2].trim()) body.push(m[2]);
  for (let j = i + 1; j < lines.length; j++) {
    const lm = lines[j].match(RX_LABEL);
    if (lm && lm[1].length <= 40 && !RX_BULLET.test(lines[j]) && !RX_ORDERED.test(lines[j])) break; // the next 'Label:' line
    body.push(lines[j]);
  }
  const content = _trimEmpty(plainToDoc(body.join('\n')).content);
  return content.length ? { label: m[1], content } : null;
}
function _trimEmpty(blocks) {
  const empty = b => b.type === 'paragraph' && !(b.content && b.content.length);
  let a = 0, z = blocks.length;
  while (a < z && empty(blocks[a])) a++;
  while (z > a && empty(blocks[z - 1])) z--;
  return blocks.slice(a, z);
}
